'use client';

import type React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import type Client from 'src/model/User';

interface MenuItem {
  title: string;
  key: string;
  content: JSX.Element;
}

interface ProfileSideMenuProps {
  user?: Client;
  menuItems: MenuItem[];
  subTitle?: string;
}

export const ProfileSideMenu: React.FC<ProfileSideMenuProps> = ({
  user,
  menuItems,
  subTitle,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchParams = new URLSearchParams(location.search);
  const activeTabKey = searchParams.get('tab') || menuItems[0].key;

  const activeItem = useMemo(
    () => menuItems.find((item) => item.key === activeTabKey) || menuItems[0],
    [menuItems, activeTabKey]
  );

  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (location.pathname === '/user-profile/' && isMobile) setIsMenuOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const handleOutsideClick = (e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node))
      setIsMenuOpen(false);
  };

  useEffect(() => {
    if (isMenuOpen) document.addEventListener('mousedown', handleOutsideClick);
    else document.removeEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMenuOpen]);

  const handleTabChange = (key: string) => {
    history.push(`${location.pathname}?tab=${key}`);
    setIsMenuOpen(false);
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <aside className="hidden min-h-full border-r border-red-200 p-4 md:block md:w-60">
        <h2 className="mb-6 text-lg font-semibold">
          {user ? `Olá ${user.name.split(' ')[0]}` : subTitle}
        </h2>
        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li
              key={item.key}
              className={`cursor-pointer rounded-md pl-3 py-2 transition-all ${
                activeItem.key === item.key
                  ? 'border-l-4 border-red-500 bg-red-50 font-semibold text-red-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:pl-4 hover:text-red-600'
              }`}
              onClick={() => handleTabChange(item.key)}
            >
              {item.title}
            </li>
          ))}
        </ul>
      </aside>

      <div className="sticky top-0 z-40 flex items-center justify-between bg-white px-2 py-2 shadow-sm md:hidden">
        <h2 className="text-lg font-semibold">
          {user ? `Olá ${user.name.split(' ')[0]}` : subTitle}
        </h2>
        <button
          onClick={() => setIsMenuOpen((v) => !v)}
          className="rounded-md bg-red-500 px-3 py-2 text-white transition-colors hover:bg-red-600"
          aria-label="Menu"
        >
          Menu
        </button>
      </div>

      <div
        className={`fixed inset-0 z-[100] bg-gray-900 transition-opacity md:hidden ${
          isMenuOpen ? 'visible opacity-50' : 'invisible opacity-0'
        }`}
      />
      <div
        ref={menuRef}
        className={`fixed left-0 top-0 z-[101] h-full w-4/5 bg-white shadow-xl transition-transform md:hidden ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h2 className="text-lg font-semibold">
            {user ? `Olá ${user.name.split(' ')[0]}` : subTitle}
          </h2>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600"
            aria-label="Fechar menu"
          >
            ×
          </button>
        </div>
        <ul className="space-y-1 p-4">
          {menuItems.map((item) => (
            <li
              key={item.key}
              className={`cursor-pointer rounded-lg p-3 transition-all ${
                activeItem.key === item.key
                  ? 'border-l-4 border-red-500 bg-red-50 font-semibold text-red-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-red-600'
              }`}
              onClick={() => handleTabChange(item.key)}
            >
              {item.title}
            </li>
          ))}
        </ul>
      </div>

      <main className="flex w-full flex-1 md:px-3">
        <div className="min-w-0 flex-1 py-4">
          <h1 className="mb-4 px-2 text-xl font-bold md:text-2xl">
            {activeItem.title}
          </h1>
          <div className="min-w-0">
            <div className="overflow-x-auto rounded-xl border">
              <div className="min-w-full">{activeItem.content}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
